import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { supabase } from './supabaseClient';
import { trackImageUpload } from './activityTracker';
import { SET_REGISTRY } from './setRegistry';
import { cropToCard } from './cardCrop';

// Optional condition close-ups for marketplace listings (Option A).
// These are OPTIONAL — they never affect has_photos / marketplace_eligible,
// which stay = (front && back). Stored at the same {userId}/{cardKey}__{slot}
// paths as front/back via imagePath(), so no new storage logic is needed.
//   slot     — storage key suffix ({userId}/{cardKey}__{slot})
//   label    — full, unambiguous label (used in the buyer gallery's flat strip)
//   short    — compact label shown in the seller form under its category heading
//   category — grouping for the seller form ('Corners' | 'Borders' | 'Surface')
export const CONDITION_SLOTS = [
  { slot: 'corner_tl',     label: 'Corner TL',      short: 'Top-Left',     category: 'Corners' },
  { slot: 'corner_tr',     label: 'Corner TR',      short: 'Top-Right',    category: 'Corners' },
  { slot: 'corner_bl',     label: 'Corner BL',      short: 'Bottom-Left',  category: 'Corners' },
  { slot: 'corner_br',     label: 'Corner BR',      short: 'Bottom-Right', category: 'Corners' },
  { slot: 'border_top',    label: 'Border Top',     short: 'Top',          category: 'Borders' },
  { slot: 'border_bottom', label: 'Border Bottom',  short: 'Bottom',       category: 'Borders' },
  { slot: 'border_left',   label: 'Border Left',    short: 'Left',         category: 'Borders' },
  { slot: 'border_right',  label: 'Border Right',   short: 'Right',        category: 'Borders' },
  { slot: 'surface_front', label: 'Surface Front',  short: 'Front',        category: 'Surface' },
  { slot: 'surface_back',  label: 'Surface Back',   short: 'Back',         category: 'Surface' },
];

// Category order + column count for the seller-form layout.
export const CONDITION_CATEGORIES = [
  { name: 'Corners', cols: 4 },
  { name: 'Borders', cols: 4 },
  { name: 'Surface', cols: 2 },
];

// Mobile photo-source handling. Mirrors the proven CollectionModal flow:
//  - iPhone: a single input with no `capture` → native iOS sheet (Photo Library
//    / Take Photo / Choose File).
//  - Android: a CK chooser ("Take Photo" / "Choose From Library") backed by TWO
//    dedicated inputs — one always-camera (capture="environment"), one always-
//    library. We never mutate the `capture` attribute at click time, which races
//    on Android Chrome and is what made "Take Photo" silently open the gallery.
const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

// Decode a File into a drawable source (ImageBitmap or HTMLImageElement),
// robust to the full-resolution JPEGs phone cameras produce. createImageBitmap
// decodes images that exceed the <img> element's decode limit on Android Chrome
// — the root cause of blank camera previews and failed resizes from full-res
// captures (a modern Android camera emits 50–108MP files). imageOrientation
// 'from-image' applies EXIF rotation to match the <img> path's behavior. Falls
// back to FileReader → Image where createImageBitmap is unavailable or throws.
export function decodeImage(file) {
  return new Promise((resolve, reject) => {
    const viaImgEl = () => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image decode failed'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('file read failed'));
      reader.readAsDataURL(file);
    };
    if (typeof createImageBitmap === 'function') {
      createImageBitmap(file, { imageOrientation: 'from-image' })
        .then(resolve)
        .catch(viaImgEl);
    } else {
      viaImgEl();
    }
  });
}

// Robust resize: decode (large-image safe) → canvas → JPEG. On any decode/encode
// failure it falls back to the ORIGINAL file so a photo is never silently
// dropped — the old path rejected, which on Android surfaced as a failed save or
// a card that uploaded its front but not its back.
// autoPortrait: rotate landscape photos 90° counterclockwise so cards always
// land portrait. Front/back only — condition close-ups (e.g. Border Top) are
// legitimately landscape and must not be rotated.
export function resizeImage(file, maxW = 600, maxH = 840, opts = {}) {
  return resizeCardPhoto(file, maxW, maxH, opts).then((r) => r.blob);
}

// resizeImage plus autoCrop (front/back photos only — see cardCrop.js):
// resolves { blob, cropped } so the UI can offer "undo crop". Re-running with
// the same file gives the same crop, so callers can preview now and re-encode
// at save time.
export function resizeCardPhoto(file, maxW = 600, maxH = 840, { autoPortrait = false, autoCrop = false } = {}) {
  return decodeImage(file).then((decoded) => new Promise((resolve) => {
    const { src, cropped } = autoCrop ? cropToCard(decoded) : { src: decoded, cropped: false };
    if (cropped && typeof decoded.close === 'function') decoded.close();
    const sw = src.width, sh = src.height;
    const rotate = autoPortrait && sw > sh;
    let w = rotate ? sh : sw;
    let h = rotate ? sw : sh;
    if (w > h) { if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; } }
    else { if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; } }
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (rotate) {
      ctx.translate(0, h);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(src, 0, 0, h, w);
    } else {
      ctx.drawImage(src, 0, 0, w, h);
    }
    if (typeof src.close === 'function') src.close(); // release ImageBitmap memory
    canvas.toBlob((blob) => resolve({ blob: blob || file, cropped: !!blob && cropped }), 'image/jpeg', 0.85);
  })).catch(() => ({ blob: file, cropped: false }));
}

// Booklet cards are landscape objects: the 2026 Galaxy Quad Autograph Book
// Card opens to a ≈2:1 spread and even its closed covers are wide (≈1.4:1 —
// the same aspect as a sideways single card, so pixel dimensions alone can't
// tell "rotate me" from "I'm a booklet"). Photos of these keys skip the
// autoPortrait rotation and render in wide frames. Scoped by key prefix to
// ONLY the 2026 Galaxy Quad Booklet Autographs (collector report, 2026-08-29);
// add a prefix here when another booklet section needs the same treatment.
export const LANDSCAPE_KEY_PREFIXES = ['2026-galaxy__auto-quad__'];
export function isLandscapeCard(cardKey) {
  return !!cardKey && LANDSCAPE_KEY_PREFIXES.some((p) => cardKey.startsWith(p));
}

export function imagePath(userId, cardKey, side) {
  const storageKey = cardKey.startsWith('2024-chrome-galaxy__')
    ? cardKey.replace('2024-chrome-galaxy__', '')
    : cardKey;
  return `${userId}/${storageKey}__${side}`;
}

export function imageUrl(userId, cardKey, side, version) {
  const resolvedUserId = process.env.REACT_APP_STORAGE_USER_OVERRIDE || userId;
  const path = imagePath(resolvedUserId, cardKey, side);
  const storageBase = process.env.REACT_APP_STORAGE_SUPABASE_URL || process.env.REACT_APP_SUPABASE_URL;
  const baseUrl = `${storageBase}/storage/v1/object/public/card-images/${path}`;
  return version ? `${baseUrl}?v=${encodeURIComponent(version)}` : baseUrl;
}

export function localImageUrl(userId, cardKey, side, version) {
  const path = imagePath(userId, cardKey, side);
  const { data } = supabase.storage.from('card-images').getPublicUrl(path);
  return version ? `${data.publicUrl}?v=${encodeURIComponent(version)}` : data.publicUrl;
}

// Checklist stock-photo fallback: thumbnails with no collector upload can fall
// back to CK's reference photos (the same objects the Set Summary gallery
// serves), marked with a gold corner triangle. Display-only — it never touches
// has_photos / marketplace eligibility, which stay driven by the collector's
// own uploads. Opt-in per set via summaryGallery.checklistFallback; only exact
// base keys ({setId}__{sectionId}__{num}) qualify, so parallel and sketch keys
// (extra __ segments) keep their empty slots.
export function referenceImageUrl(cardKey, side) {
  const parts = cardKey.split('__');
  if (parts.length !== 3) return null;
  const reg = SET_REGISTRY.find(s => s.id === parts[0]);
  // The fallback config rides on summaryGallery (checklistFallback: true) for
  // sets that also publish a Set Summary photo gallery, or stands alone as
  // reg.checklistFallback ({ storageBase, sections }) for sets whose reference
  // photos should ONLY back the checklist.
  const gallery = reg?.summaryGallery;
  const fb = (gallery?.checklistFallback ? gallery : null) || reg?.checklistFallback || null;
  if (!fb || !fb.sections.includes(parts[1])) return null;
  // Same legacy rule as imagePath: 2024 Chrome Galaxy objects are stored
  // without the set prefix.
  const storageKey = cardKey.startsWith('2024-chrome-galaxy__')
    ? cardKey.replace('2024-chrome-galaxy__', '')
    : cardKey;
  return `${fb.storageBase}/${storageKey}__${side}`;
}

// Removes a single side (front/back) for a card in storage and updates DB flags.
// Used by both CardImage's own delete UI and CollectionModal's REMOVE button.
// Routes through updateMarketplaceEligibility so photos_updated_at is stamped (cache fix).
export async function handlePhotoRemove(userId, cardKey, side, currentHasFront, currentHasBack) {
  await supabase.storage.from('card-images').remove([imagePath(userId, cardKey, side)]);
  const newHasFront = side === 'front' ? false : currentHasFront;
  const newHasBack  = side === 'back'  ? false : currentHasBack;
  await updateMarketplaceEligibility(userId, cardKey, newHasFront, newHasBack);
}

// Remove an OPTIONAL condition photo. Unlike handlePhotoRemove (front/back),
// this does NOT recompute marketplace_eligible — condition shots never gate a
// listing. It only deletes the storage object and bumps photos_updated_at so
// the buyer side's cached URL invalidates.
export async function removeExtraPhoto(userId, cardKey, slot) {
  await supabase.storage.from('card-images').remove([imagePath(userId, cardKey, slot)]);
  try {
    const stamp = new Date().toISOString();
    await supabase.from('collections')
      .update({ photos_updated_at: stamp })
      .eq('user_id', userId)
      .eq('card_key', cardKey);
  } catch (err) {
    console.error('removeExtraPhoto stamp error:', err);
  }
}

export async function updateMarketplaceEligibility(userId, cardKey, hasFront, hasBack) {
  try {
    // Strict per-record (Option A): a record is eligible iff IT has both photos
    // AND ITS OWN status is for_sale/trade. No base↔serial cross-propagation and
    // no special-casing of the base parallel — each card_key stands alone.
    const bothPhotos = hasFront && hasBack;
    const photoStamp = new Date().toISOString();
    const { data: row } = await supabase.from('collections')
      .select('status')
      .eq('user_id', userId)
      .eq('card_key', cardKey)
      .single();
    const isListed = row?.status === 'for_sale' || row?.status === 'trade';
    await supabase.from('collections')
      .update({ marketplace_eligible: bothPhotos && isListed, has_photos: bothPhotos, photos_updated_at: photoStamp })
      .eq('user_id', userId)
      .eq('card_key', cardKey);
  } catch (err) {
    console.error('updateMarketplaceEligibility error:', err);
  }
}

// Shared photo-source picker for an arbitrary set of slots. onFile(slot, file)
// fires when the user picks/takes a photo. Returns openPicker(slot) plus
// `inputs` and `chooser` JSX the host renders once. Defaults to front/back so
// existing callers keep working unchanged.
export function usePhotoSource(onFile, slots = ['front', 'back']) {
  const [chooserSlot, setChooserSlot] = useState(null);
  // refs.current[slot] = { single, camera, library } — created lazily.
  const refs = useRef({});
  const getRefs = (slot) => {
    if (!refs.current[slot]) {
      refs.current[slot] = { single: { current: null }, camera: { current: null }, library: { current: null } };
    }
    return refs.current[slot];
  };

  const openPicker = useCallback((slot) => {
    const r = getRefs(slot);
    if (isIOS) {
      if (r.single.current) { r.single.current.value = ''; r.single.current.removeAttribute('capture'); r.single.current.click(); }
    } else {
      setChooserSlot(slot);
    }
  }, []); // getRefs is stable (refs is a ref)

  const onChange = (slot) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file / re-opening after back-out
    if (file) onFile(slot, file);
  };

  const hidden = { display: 'none' };
  const inputs = (
    <>
      {slots.map((slot) => {
        const r = getRefs(slot);
        return (
          <Fragment key={slot}>
            <input ref={(el) => { r.single.current = el; }}  type="file" accept="image/*" style={hidden} onChange={onChange(slot)} onClick={e => e.stopPropagation()} />
            <input ref={(el) => { r.camera.current = el; }}  type="file" accept="image/*" capture="environment" style={hidden} onChange={onChange(slot)} onClick={e => e.stopPropagation()} />
            <input ref={(el) => { r.library.current = el; }} type="file" accept="image/*" style={hidden} onChange={onChange(slot)} onClick={e => e.stopPropagation()} />
          </Fragment>
        );
      })}
    </>
  );

  const chooser = (chooserSlot && !isIOS) ? (
    <div onClick={(e) => { e.stopPropagation(); setChooserSlot(null); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--panel)', border: '1px solid var(--cyan)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 360, boxShadow: '0 0 24px rgba(var(--cyan-rgb),0.3)' }}>
        <div style={{ fontFamily: "'Orbitron',monospace", color: 'var(--cyan)', fontSize: 13, letterSpacing: 2, textAlign: 'center', marginBottom: 18, textTransform: 'uppercase' }}>
          {chooserSlot} Photo
        </div>
        <button type="button"
          onClick={() => { const r = getRefs(chooserSlot).camera; if (r.current) { r.current.value = ''; r.current.click(); } setChooserSlot(null); }}
          style={{ display: 'block', width: '100%', padding: '14px 20px', marginBottom: 12, background: 'transparent', border: '1px solid var(--gold)', color: 'var(--gold)', fontFamily: "'Share Tech Mono',monospace", fontSize: 15, letterSpacing: 1.5, textTransform: 'uppercase', borderRadius: 8, cursor: 'pointer' }}>
          📷 Take Photo
        </button>
        <button type="button"
          onClick={() => { const r = getRefs(chooserSlot).library; if (r.current) { r.current.value = ''; r.current.click(); } setChooserSlot(null); }}
          style={{ display: 'block', width: '100%', padding: '14px 20px', marginBottom: 12, background: 'transparent', border: '1px solid var(--cyan)', color: 'var(--cyan)', fontFamily: "'Share Tech Mono',monospace", fontSize: 15, letterSpacing: 1.5, textTransform: 'uppercase', borderRadius: 8, cursor: 'pointer' }}>
          🖼️ Choose From Library
        </button>
        <button type="button" onClick={() => setChooserSlot(null)}
          style={{ display: 'block', width: '100%', padding: '12px 20px', background: 'transparent', border: '1px solid var(--muted-2)', color: 'var(--muted-2)', fontFamily: "'Share Tech Mono',monospace", fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', borderRadius: 8, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  ) : null;

  return { openPicker, inputs, chooser };
}

// True on a desktop pointer (mouse/trackpad) — where drag and drop works and
// labels can say "click or drop". Phones keep their tap labels.
export const canDropPhotos = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

// Drag and drop for a front/back photo pair (ported from CK 3.0, advisor item
// 10). A photo dropped on a [data-side] tile fills that side; dropped anywhere
// else in the zone it fills the first empty side. Two photos at once fill the
// front, then the back. `have` = { front, back } booleans; spread `zoneProps`
// on the container; `dragOver` is 'front' | 'back' | 'any' | null.
export function usePhotoDrop(onFile, have, busy = false) {
  const [dragOver, setDragOver] = useState(null);
  const isImage = (f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name);
  const sideOf = (e) => e.target.closest?.('[data-side]')?.dataset.side;
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const target = sideOf(e);
    setDragOver(null);
    if (busy) return;
    const files = [...(e.dataTransfer?.files || [])].filter(isImage);
    if (!files.length) return;
    if (files.length > 1) { await onFile('front', files[0]); await onFile('back', files[1]); return; }
    await onFile(target || (!have.front ? 'front' : !have.back ? 'back' : 'front'), files[0]);
  };
  const zoneProps = {
    onDragOver: (e) => { e.preventDefault(); setDragOver(sideOf(e) || 'any'); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); },
    onDrop,
  };
  // Is this side lit up as the drop target right now?
  const isTarget = (side) => dragOver === side || (dragOver === 'any' && (!have[side] || (have.front && have.back && side === 'front')));
  return { dragOver, zoneProps, isTarget };
}

// Spread on a modal backdrop so a photo dropped off-target never opens the
// file in the tab.
export const swallowDrops = { onDragOver: (e) => e.preventDefault(), onDrop: (e) => e.preventDefault() };

// Gold corner marker for CK reference photos on enlarged views (hover preview,
// photo modal) — big enough to carry the CK letters, unlike the bare triangle
// on the small thumbnails. Text uses var(--bg) so it stays readable on both
// themes' gold.
function RefCorner({ size = 36 }) {
  return (
    <div style={{ position: 'absolute', bottom: 0, right: 0, width: `${size}px`, height: `${size}px`, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', bottom: 0, right: 0, width: 0, height: 0, borderLeft: `${size}px solid transparent`, borderBottom: `${size}px solid var(--gold)` }} />
      <span style={{ position: 'absolute', bottom: '3px', right: '3px', fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', fontWeight: 700, color: 'var(--bg)', letterSpacing: '1px' }}>CK</span>
    </div>
  );
}

export function CardThumbnail({ userId, cardKey, isOwner, onImageChange, zoom, stateColor, photosUpdatedAt }) {
  const [frontUrl, setFrontUrl] = useState(null);
  const [backUrl, setBackUrl] = useState(null);
  // CK reference fallback ({ f, b }) — display-only stand-in while the collector
  // has no upload of their own; frontUrl/backUrl stay strictly their uploads.
  const [refUrls, setRefUrls] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showHover, setShowHover] = useState(false);
  const hoverTimer = useRef(null);
  const wrapRef = useRef(null);
  const justUploaded = useRef({});

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    let cancelled = false;

    const tryLoadUrls = (f, b) => new Promise((resolve) => {
      const ci = new Image();
      ci.onload = () => {
        if (cancelled) return resolve(null);
        const ci2 = new Image();
        ci2.onload = () => { if (!cancelled) resolve({ f, b }); else resolve(null); };
        ci2.onerror = () => { if (!cancelled) resolve({ f, b: null }); else resolve(null); };
        ci2.src = b;
      };
      ci.onerror = () => resolve(null);
      ci.src = f;
    });

    const tryLoadAt = (key) =>
      tryLoadUrls(localImageUrl(userId, key, 'front', photosUpdatedAt), localImageUrl(userId, key, 'back', photosUpdatedAt))
        .then(r => (r ? { key, ...r } : null));

    const observer = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();

      // Strict per-record (Option A): a thumbnail shows ONLY this exact card_key's
      // photos — no base↔serial stand-ins. "No photo" in the checklist therefore
      // means "this exact copy has no photo," which matches marketplace eligibility.
      const main = await tryLoadAt(cardKey);
      if (main) {
        if (!cancelled) { setFrontUrl(main.f); setBackUrl(main.b); setRefUrls(null); }
        return;
      }

      if (!cancelled) { setFrontUrl(null); setBackUrl(null); }

      // No upload of their own — fall back to the CK reference photos where the
      // set opts in (base cards of sets with summaryGallery.checklistFallback).
      const refFront = referenceImageUrl(cardKey, 'front');
      if (refFront) {
        const ref = await tryLoadUrls(refFront, referenceImageUrl(cardKey, 'back'));
        if (!cancelled) setRefUrls(ref);
      } else if (!cancelled) {
        setRefUrls(null);
      }
    }, { rootMargin: '200px' });

    observer.observe(node);
    return () => { cancelled = true; observer.disconnect(); };
  }, [cardKey, userId, photosUpdatedAt]);

  const checkImages = async () => {
    const stamp = Date.now();
    const f = localImageUrl(userId, cardKey, 'front', stamp);
    const b = localImageUrl(userId, cardKey, 'back', stamp);
    const ci = new Image(); ci.onload = () => setFrontUrl(f); ci.onerror = () => setFrontUrl(null); ci.src = f;
    const ci2 = new Image(); ci2.onload = () => setBackUrl(b); ci2.onerror = () => setBackUrl(null); ci2.src = b;
  };

  const isLandscape = isLandscapeCard(cardKey);

  const handleFile = async (side, file) => {
    if (!file) return;
    setLoading(true);
    try {
      const resized = await resizeImage(file, 600, 840, { autoPortrait: !isLandscape });
      await supabase.storage.from('card-images').upload(imagePath(userId, cardKey, side), resized, { upsert: true, contentType: 'image/jpeg' });
      trackImageUpload(userId);
      justUploaded.current[side] = true;
      await checkImages();
      // justUploaded, not just frontUrl/backUrl: a two-photo drop uploads back
      // right after front, before the front's state has caught up.
      await updateMarketplaceEligibility(userId, cardKey, justUploaded.current.front || !!frontUrl, justUploaded.current.back || !!backUrl);
      if (onImageChange) onImageChange();
    } catch (err) {
      console.error('[CardThumbnail] photo upload failed:', err);
      alert('Could not process that photo. Please try a different one.');
    } finally {
      setLoading(false);
    }
  };

  const { openPicker, inputs: photoInputs, chooser: photoChooser } = usePhotoSource(handleFile);
  const { zoneProps: dropZone, isTarget: isDropTarget } = usePhotoDrop(handleFile, { front: !!frontUrl, back: !!backUrl }, loading);

  const handleDelete = async (e, side) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${side} image?`)) return;
    justUploaded.current[side] = false;
    setLoading(true);
    await supabase.storage.from('card-images').remove([imagePath(userId, cardKey, side)]);
    // Set null directly — don't call checkImages() which hits CDN cache and restores deleted images
    if (side === 'front') setFrontUrl(null); else setBackUrl(null);
    await updateMarketplaceEligibility(userId, cardKey, side === 'front' ? false : !!frontUrl, side === 'back' ? false : !!backUrl);
    setLoading(false);
    if (onImageChange) onImageChange();
  };

  const dispFront = frontUrl || refUrls?.f || null;
  const dispBack = backUrl || refUrls?.b || null;
  const refShown = !frontUrl && !!refUrls?.f;
  const triSize = zoom ? '16px' : '10px';

  return (
    <>
      {isOwner && photoInputs}
      <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}
        onMouseEnter={() => { if (!dispFront) return; hoverTimer.current = setTimeout(() => setShowHover(true), 300); }}
        onMouseLeave={() => { clearTimeout(hoverTimer.current); setShowHover(false); }}>
        <div style={{ ...T.wrap, position: 'relative', cursor: (dispFront || dispBack || isOwner) ? 'pointer' : 'default', borderColor: dispFront ? (stateColor || 'rgba(var(--gold-rgb),0.3)') : 'var(--border)', ...(isLandscape ? { width: zoom ? '126px' : '100px', height: zoom ? '63px' : '50px' } : { width: zoom ? '90px' : '36px', height: zoom ? '126px' : '50px' }) }}
          onClick={(e) => { e.stopPropagation(); setShowHover(false); if (isOwner || dispFront || dispBack) setShowModal(true); }}>
          {loading ? <div style={T.loading}>⟳</div> : dispFront ? <img src={dispFront} alt="card" style={T.img} /> : <div style={T.placeholder}>{isOwner ? '+' : ''}</div>}
          {!loading && refShown && <div style={{ position: 'absolute', bottom: 0, right: 0, width: 0, height: 0, borderLeft: `${triSize} solid transparent`, borderBottom: `${triSize} solid var(--gold)` }} />}
        </div>
        {showHover && dispFront && <div style={T.hover} onClick={e => e.stopPropagation()}>
          <div style={{ position: 'relative' }}>
            <img src={dispFront} alt="preview" style={{ width: '100%', borderRadius: '2px', display: 'block' }} />
            {refShown && <RefCorner />}
          </div>
        </div>}
      </div>
      {showModal && (
        <div style={M.overlay} onClick={(e) => { e.stopPropagation(); setShowModal(false); }} {...swallowDrops}>
          <div style={M.box} onClick={e => e.stopPropagation()}>
            <button style={M.closeBtn} onClick={() => setShowModal(false)}>✕</button>
            <div style={M.row} {...(isOwner ? dropZone : {})}>
              {['front', 'back'].map(side => {
                const url = side === 'front' ? frontUrl : backUrl;
                const refUrl = url ? null : (side === 'front' ? refUrls?.f : refUrls?.b) || null;
                return (
                  <div key={side} data-side={isOwner ? side : undefined} style={M.col}>
                    <div style={M.label}>{side.toUpperCase()}</div>
                    {(url || refUrl) ? (
                      <div style={{ position: 'relative', width: '100%', ...(isOwner && isDropTarget(side) ? M.dropRing : {}) }}>
                        <img src={url || refUrl} alt={side} style={{ ...M.img, display: 'block' }} />
                        {refUrl && <RefCorner size={44} />}
                        {isOwner && isDropTarget(side) && <div style={M.dropHint}>DROP TO REPLACE</div>}
                      </div>
                    ) : <div style={{ ...M.empty, ...(isLandscape ? { aspectRatio: '2/1' } : {}), ...(isOwner && isDropTarget(side) ? M.dropRing : {}) }}>{isOwner ? (isDropTarget(side) ? 'DROP IT HERE' : canDropPhotos ? `No ${side} image · drop a photo here` : `No ${side} image`) : 'Not uploaded'}</div>}
                    {refUrl && <div style={M.refNote}>CK REFERENCE PHOTO{isOwner ? ' — UPLOAD YOUR OWN TO REPLACE IT' : ''}</div>}
                    {isOwner && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                        <button style={M.uploadBtn} onClick={(e) => { e.stopPropagation(); openPicker(side); }}>{url ? `REPLACE ${side.toUpperCase()}` : `UPLOAD ${side.toUpperCase()}`}</button>
                        {url && <button style={M.deleteBtn} onClick={(e) => handleDelete(e, side)}>REMOVE {side.toUpperCase()}</button>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {isOwner && (
              <ConditionPhotosEditor userId={userId} cardKey={cardKey} stateColor={stateColor} photosUpdatedAt={photosUpdatedAt} onImageChange={onImageChange} />
            )}
            <button onClick={() => setShowModal(false)} style={{ display: 'block', width: '100%', marginTop: '24px', padding: '14px', fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '3px', background: 'transparent', border: '1px solid var(--cyan)', color: 'var(--cyan)', cursor: 'pointer', borderRadius: '2px' }}>CLOSE</button>
          </div>
        </div>
      )}
      {photoChooser}
    </>
  );
}

const T = {
  wrap: { flexShrink: 0, border: '1px solid', borderRadius: '2px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-3)', transition: 'border-color 0.2s' },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  placeholder: { fontFamily: "'Share Tech Mono', monospace", fontSize: '16px', color: 'var(--border)' },
  loading: { fontFamily: "'Share Tech Mono', monospace", fontSize: '14px', color: 'var(--muted-2)' },
  hover: { position: 'absolute', left: '44px', top: '50%', transform: 'translateY(-50%)', width: '300px', background: 'var(--panel-deep)', border: '1px solid rgba(var(--gold-rgb),0.4)', borderRadius: '4px', padding: '6px', boxShadow: '0 8px 32px rgba(0,0,0,0.8)', zIndex: 99999, pointerEvents: 'none' },
};

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '20px' },
  box: { position: 'relative', background: 'var(--panel-deep)', border: '1px solid var(--border)', borderRadius: '4px', padding: '32px', maxWidth: '800px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  closeBtn: { position: 'absolute', top: 'calc(16px + max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px)))', right: '16px', background: 'none', border: '1px solid var(--border)', color: 'var(--muted-2)', cursor: 'pointer', fontSize: '14px', width: '32px', height: '32px', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  row: { display: 'flex', gap: '24px', flexWrap: 'wrap', justifyContent: 'center' },
  col: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', flex: '1', minWidth: '200px', maxWidth: '340px' },
  label: { fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '3px', color: 'var(--muted-2)' },
  refNote: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', letterSpacing: '1px', color: 'var(--gold)', textAlign: 'center' },
  img: { width: '100%', borderRadius: '4px', border: '1px solid var(--border)' },
  empty: { width: '100%', aspectRatio: '2.5/3.5', background: 'var(--bg-3)', border: '1px dashed var(--border)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted-2)' },
  uploadBtn: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '7px 14px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted-2)', cursor: 'pointer', borderRadius: '2px', width: '100%' },
  deleteBtn: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '7px 14px', background: 'transparent', border: '1px solid rgba(var(--red-rgb),0.4)', color: 'var(--red)', cursor: 'pointer', borderRadius: '2px', width: '100%' },
  dropRing: { outline: '2px solid var(--cyan)', outlineOffset: '2px', borderRadius: '4px', background: 'rgba(var(--cyan-rgb),0.08)' },
  dropHint: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', color: '#fff', fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', letterSpacing: '1px', borderRadius: '4px', pointerEvents: 'none' },
};

export function SerialPhotoPanel({ userId, cardKey, stateColor, photosUpdatedAt, onImageChange, listingStatus }) {
  const [frontUrl, setFrontUrl] = useState(null);
  const [backUrl, setBackUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewSide, setViewSide] = useState(null); // 'front' | 'back' | null — enlarged viewer
  const justUploaded = useRef({}); // see CardThumbnail: a two-photo drop outruns state

  useEffect(() => {
    const chk = (url, set) => { const img = new Image(); img.onload = () => set(url); img.onerror = () => set(null); img.src = url; };
    chk(imageUrl(userId, cardKey, 'front', photosUpdatedAt), setFrontUrl);
    chk(imageUrl(userId, cardKey, 'back', photosUpdatedAt), setBackUrl);
  }, [cardKey, userId, photosUpdatedAt]);

  const isLandscape = isLandscapeCard(cardKey);

  const handleFile = async (side, file) => {
    if (!file) return;
    setLoading(true);
    try {
      const resized = await resizeImage(file, 600, 840, { autoPortrait: !isLandscape });
      await supabase.storage.from('card-images').upload(imagePath(userId, cardKey, side), resized, { upsert: true, contentType: 'image/jpeg' });
      trackImageUpload(userId);
      justUploaded.current[side] = true;
      const newUrl = localImageUrl(userId, cardKey, side, Date.now());
      if (side === 'front') setFrontUrl(newUrl); else setBackUrl(newUrl);
      await updateMarketplaceEligibility(userId, cardKey, justUploaded.current.front || !!frontUrl, justUploaded.current.back || !!backUrl);
      if (onImageChange) onImageChange();
    } catch (err) {
      console.error('[SerialPhotoPanel] photo upload failed:', err);
      alert('Could not process that photo. Please try a different one.');
    } finally {
      setLoading(false);
    }
  };

  const { openPicker, inputs: photoInputs, chooser: photoChooser } = usePhotoSource(handleFile);
  const { zoneProps: dropZone, isTarget: isDropTarget } = usePhotoDrop(handleFile, { front: !!frontUrl, back: !!backUrl }, loading);

  const handleDelete = async (e, side) => {
    e.stopPropagation();
    if (!window.confirm('Remove ' + side + ' photo?')) return;
    justUploaded.current[side] = false;
    setLoading(true);
    await supabase.storage.from('card-images').remove([imagePath(userId, cardKey, side)]);
    if (side === 'front') { setFrontUrl(null); await updateMarketplaceEligibility(userId, cardKey, false, !!backUrl); }
    else { setBackUrl(null); await updateMarketplaceEligibility(userId, cardKey, !!frontUrl, false); }
    setLoading(false);
    if (onImageChange) onImageChange();
  };

  const boxW = isLandscape ? '112px' : '70px';
  const boxH = isLandscape ? '56px' : '98px';

  const Side = ({ side, url }) => (
    <div data-side={side} style={{ display:'flex', flexDirection:'column', alignItems:'center' }} onClick={e => e.stopPropagation()}>
      <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'.12em', color:'var(--muted-2)', marginBottom:'5px' }}>{side.toUpperCase()}</div>
      <div title={canDropPhotos ? (url ? 'Drop a photo to replace' : 'Click or drop a photo') : undefined}
        style={{ width:boxW, height:boxH, border: isDropTarget(side) ? '2px solid var(--cyan)' : `1px solid ${url ? (stateColor||'rgba(var(--gold-rgb),0.4)') : 'var(--border)'}`, borderRadius:'2px', overflow:'hidden', background: isDropTarget(side) ? 'rgba(var(--cyan-rgb),0.08)' : 'var(--bg-3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
        onClick={() => { if (url) setViewSide(side); else openPicker(side); }}>
        {loading ? <span style={{ color:'var(--muted-2)', fontSize:'14px' }}>⟳</span> : url ? <img src={url} alt={side} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ color:'var(--border)', fontSize:'20px' }}>+</span>}
      </div>
      <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'.08em', padding:'4px 6px', borderRadius:'2px', cursor:'pointer', background:'transparent', border:'1px solid var(--border)', color:'var(--muted-2)', width:boxW, marginTop:'4px' }} onClick={() => openPicker(side)}>{url ? 'REPLACE' : 'UPLOAD'}</button>
      {url && <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'.08em', padding:'4px 6px', borderRadius:'2px', cursor:'pointer', background:'transparent', border:'1px solid rgba(var(--red-rgb),0.5)', color:'var(--red)', width:boxW, marginTop:'3px' }} onClick={e => handleDelete(e, side)}>REMOVE</button>}
    </div>
  );

  const viewUrl = viewSide === 'front' ? frontUrl : viewSide === 'back' ? backUrl : null;

  const listingNeedsPhotos = (listingStatus === 'for_sale' || listingStatus === 'trade') && (!frontUrl || !backUrl);

  return (
    <div onClick={e => e.stopPropagation()}>
      {listingNeedsPhotos && (
        <div style={{ display:'flex', gap:'6px', alignItems:'flex-start', background:'rgba(var(--gold-rgb),0.08)', border:'1px solid var(--gold)', borderRadius:'4px', padding:'6px 8px', marginBottom:'10px', fontFamily:"'Share Tech Mono',monospace", fontSize:'9px', color:'var(--gold)', lineHeight:1.4 }}>
          <span style={{ fontSize:'10px', lineHeight:1 }}>⚠</span>
          <span>Won’t appear in the Marketplace until this {listingStatus === 'for_sale' ? 'listing' : 'trade'} has both a FRONT and BACK photo. (Condition close-ups don’t count.)</span>
        </div>
      )}
      <div style={{ display:'flex', gap:'16px', alignItems:'flex-start' }} {...dropZone}>
        <Side side="front" url={frontUrl} />
        <Side side="back" url={backUrl} />
        {photoInputs}
        {photoChooser}
        {viewSide && viewUrl && (
          <div onClick={() => setViewSide(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100000, padding:20 }}>
            <img src={viewUrl} alt={viewSide} style={{ maxWidth:'100%', maxHeight:'90vh', borderRadius:4, border:'1px solid var(--border)' }} onClick={e => e.stopPropagation()} />
          </div>
        )}
      </div>
      <ConditionPhotosEditor userId={userId} cardKey={cardKey} stateColor={stateColor} photosUpdatedAt={photosUpdatedAt} onImageChange={onImageChange} />
    </div>
  );
}

// Shared, immediate-upload editor for the optional condition close-ups
// (corners / borders / surface). Used by SerialPhotoPanel + CardThumbnail,
// where the cardKey already exists. CollectionModal uses its own
// deferred-at-save flow because in 'new' mode the cardKey isn't known yet.
// Condition photos are OPTIONAL and never affect marketplace_eligible.
export function ConditionPhotosEditor({ userId, cardKey, stateColor, photosUpdatedAt, onImageChange }) {
  const [urls, setUrls] = useState({});        // slot -> url | undefined
  const [loadingSlot, setLoadingSlot] = useState(null);
  const [viewSlot, setViewSlot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    CONDITION_SLOTS.forEach(({ slot }) => {
      const u = imageUrl(userId, cardKey, slot, photosUpdatedAt);
      const img = new Image();
      img.onload = () => { if (!cancelled) setUrls((p) => ({ ...p, [slot]: u })); };
      img.onerror = () => { if (!cancelled) setUrls((p) => { const n = { ...p }; delete n[slot]; return n; }); };
      img.src = u;
    });
    return () => { cancelled = true; };
  }, [userId, cardKey, photosUpdatedAt]);

  const handleFile = async (slot, file) => {
    if (!file) return;
    setLoadingSlot(slot);
    try {
      const resized = await resizeImage(file, 1600, 1600); // detail matters for condition
      await supabase.storage.from('card-images').upload(imagePath(userId, cardKey, slot), resized, { upsert: true, contentType: 'image/jpeg' });
      trackImageUpload(userId);
      // Bump cache-buster so the buyer side invalidates; do NOT recompute eligibility.
      try { await supabase.from('collections').update({ photos_updated_at: new Date().toISOString() }).eq('user_id', userId).eq('card_key', cardKey); } catch (e) { /* non-fatal */ }
      setUrls((p) => ({ ...p, [slot]: localImageUrl(userId, cardKey, slot, Date.now()) }));
      if (onImageChange) onImageChange();
    } catch (err) {
      console.error('[ConditionPhotosEditor] upload failed:', err);
      alert('Could not process that photo. Please try a different one.');
    } finally {
      setLoadingSlot(null);
    }
  };

  const { openPicker, inputs, chooser } = usePhotoSource(handleFile, CONDITION_SLOTS.map((s) => s.slot));

  const handleDelete = async (e, slot) => {
    e.stopPropagation();
    if (!window.confirm('Remove ' + slot + ' photo?')) return;
    setLoadingSlot(slot);
    await removeExtraPhoto(userId, cardKey, slot);
    setUrls((p) => { const n = { ...p }; delete n[slot]; return n; });
    setLoadingSlot(null);
    if (onImageChange) onImageChange();
  };

  const accent = stateColor || 'rgba(var(--gold-rgb),0.4)';
  const viewUrl = viewSlot ? urls[viewSlot] : null;

  return (
    <div onClick={e => e.stopPropagation()} style={{ marginTop:'12px', paddingTop:'10px', borderTop:'1px solid var(--border)' }}>
      <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'8px' }}>
        CONDITION DETAIL <span style={{ color:'var(--border-2)' }}>· OPTIONAL</span>
      </div>
      {CONDITION_CATEGORIES.map(({ name }) => {
        const slots = CONDITION_SLOTS.filter((s) => s.category === name);
        const filled = slots.filter((s) => urls[s.slot]).length;
        return (
          <div key={name} style={{ marginBottom:'8px' }}>
            <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'8px', letterSpacing:'.1em', color:'var(--muted-2)', marginBottom:'4px', textTransform:'uppercase' }}>
              {name} · {filled}/{slots.length}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(52px, 1fr))', gap:'6px' }}>
              {slots.map(({ slot, short }) => {
                const url = urls[slot];
                return (
                  <div key={slot} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'3px' }}>
                    <div
                      onClick={() => { if (url) setViewSlot(slot); else openPicker(slot); }}
                      style={{ width:'100%', aspectRatio:'1/1', border:`1px ${url ? 'solid' : 'dashed'} ${url ? accent : 'var(--border)'}`, borderRadius:'2px', overflow:'hidden', background:'var(--bg-3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                      {loadingSlot === slot ? <span style={{ color:'var(--muted-2)', fontSize:'12px' }}>⟳</span> : url ? <img src={url} alt={slot} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ color:'var(--border)', fontSize:'16px' }}>+</span>}
                    </div>
                    <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'6px', letterSpacing:'.04em', color:'var(--muted-2)', textAlign:'center', lineHeight:1.1 }}>{short.toUpperCase()}</div>
                    {url && <button onClick={(e) => handleDelete(e, slot)} style={{ fontFamily:"'Orbitron',monospace", fontSize:'5px', letterSpacing:'.06em', padding:'2px 4px', borderRadius:'2px', cursor:'pointer', background:'transparent', border:'1px solid rgba(var(--red-rgb),0.5)', color:'var(--red)', width:'100%' }}>REMOVE</button>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {inputs}
      {chooser}
      {viewSlot && viewUrl && (
        <div onClick={() => setViewSlot(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100000, padding:20 }}>
          <img src={viewUrl} alt={viewSlot} style={{ maxWidth:'100%', maxHeight:'90vh', borderRadius:4, border:'1px solid var(--border)' }} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
